import { featureFlags } from '@seen/core/featureFlags'
import {
  formatRafScore,
  type RafBreakdown,
  type RafDiagnosis,
  type RafDiagnosisSource,
  type RafModel,
  type RafTerm,
} from '@seen/request-types/rafScore'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  cn,
} from '@seen/ui'
import { IconChevronDown, IconInfoCircle } from '@tabler/icons-react'
import { sumBy } from 'lodash-es'
import { type JSX, useEffect, useState } from 'react'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useHashMatch } from '#src/hooks/useHashMatch.ts'
import { useScrollHighlight } from '#src/hooks/useScrollHighlight.ts'
import { RISK_CONTRIBUTORS_ANCHOR } from '#src/pages/participants/conditions/riskContributorsAnchor.ts'

const HEADINGS = {
  demographic: 'Demographics',
  hcc: 'Conditions',
  interaction: 'Disease Interactions',
} as const

// Contributor, Seen Health, external, coefficient. Every row shares this grid
// so the dots and numbers line up down the section instead of trailing each
// line. The two dot columns are sized to the header labels above them, not to
// the dots, which are far narrower.
const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_68px_68px_64px] gap-x-3'

const MODELS = ['V28', 'V22'] as const

function modelLabel(model: RafModel): string {
  return `CMS-HCC ${model.toLowerCase()}`
}

const SOURCE_EXPLANATIONS: Record<
  RafDiagnosisSource,
  { documented: string; absent: string }
> = {
  seen: {
    documented: 'This was diagnosed at Seen Health',
    absent: 'This was not diagnosed at Seen Health',
  },
  external: {
    documented: 'This was diagnosed externally',
    absent: 'This was not diagnosed externally',
  },
}

function formatCoefficient(coefficient: number): string {
  return coefficient.toFixed(3)
}

function hasTerms(breakdown: RafBreakdown | null | undefined): boolean {
  return (breakdown?.terms.length ?? 0) > 0
}

function breakdownFor(
  selected: RafModel,
  v28: RafBreakdown | null,
  v22: RafBreakdown | null | undefined
): RafBreakdown | null {
  const preferred = selected === 'V22' ? v22 : v28
  if (hasTerms(preferred)) {
    return preferred ?? null
  }
  if (hasTerms(v28)) {
    return v28
  }
  return hasTerms(v22) ? (v22 ?? null) : null
}

export function ParticipantRiskContributors({
  breakdown,
  breakdownV22,
  score,
  cmsScore,
  defaultExpanded,
}: {
  breakdown: RafBreakdown | null
  breakdownV22?: RafBreakdown | null
  score?: number
  cmsScore?: number | null
  // Only the stories set this, so Chromatic snapshots more than the header.
  defaultExpanded?: boolean
}): JSX.Element | null {
  const deepLinked = useHashMatch(RISK_CONTRIBUTORS_ANCHOR)
  const [openValue, setOpenValue] = useState(
    defaultExpanded ? RISK_CONTRIBUTORS_ANCHOR : ''
  )
  const v28Available = hasTerms(breakdown)
  const v22Available = hasTerms(breakdownV22)
  const [selectedModel, setSelectedModel] = useState<RafModel>(
    v28Available ? 'V28' : 'V22'
  )
  const { ref: highlightRef, highlightClassName } =
    useScrollHighlight(deepLinked)
  const rumStableActionNames = useFeatureFlag(featureFlags.rumStableActionNames)

  // The facesheet header that links here renders on this page too, so following
  // that link usually changes the hash without remounting the page. Opening on
  // the hash rather than through `defaultValue` covers both arrivals.
  useEffect(() => {
    if (deepLinked) {
      setOpenValue(RISK_CONTRIBUTORS_ANCHOR)
    }
  }, [deepLinked])

  const activeBreakdown = breakdownFor(selectedModel, breakdown, breakdownV22)

  useEffect(() => {
    if (activeBreakdown && selectedModel !== activeBreakdown.model) {
      setSelectedModel(activeBreakdown.model)
    }
  }, [activeBreakdown, selectedModel])

  if (!activeBreakdown) {
    return null
  }

  const byKind = (kind: RafTerm['kind']): RafTerm[] =>
    activeBreakdown.terms.filter((term) => term.kind === kind)
  const demographics = byKind('demographic')
  const conditions = byKind('hcc')
  const interactions = byKind('interaction')

  return (
    // Bordered like the condition tables above it, which render inside
    // `rounded-md border` — without it the section reads as floating between
    // two contained ones.
    <Accordion
      type="single"
      collapsible
      value={openValue}
      onValueChange={setOpenValue}
      id={RISK_CONTRIBUTORS_ANCHOR}
      ref={highlightRef}
      className={cn('rounded-md border', highlightClassName)}
    >
      <AccordionItem value={RISK_CONTRIBUTORS_ANCHOR} className="border-none">
        {/* The model switch is a sibling of the trigger rather than a child of
            it: the trigger renders a button, and a button cannot nest one. */}
        <div className="flex items-center gap-4 pr-4">
          <AccordionTrigger
            hideChevron
            className="w-auto flex-none gap-2 p-4 hover:no-underline"
            data-dd-action-name={
              rumStableActionNames
                ? openValue === RISK_CONTRIBUTORS_ANCHOR
                  ? 'conditions.risk_breakdown.collapse'
                  : 'conditions.risk_breakdown.expand'
                : undefined
            }
          >
            <h2 className="whitespace-nowrap">Risk contributors</h2>
            <IconChevronDown className="size-4 shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </AccordionTrigger>
          {v28Available && v22Available ? (
            <fieldset className="inline-flex h-8 items-center gap-1 rounded-md bg-frame p-1 text-frame-foreground shadow-inner">
              <legend className="sr-only">CMS-HCC model</legend>
              {MODELS.map((model) => (
                <button
                  key={model}
                  type="button"
                  aria-pressed={selectedModel === model}
                  className={cn(
                    'whitespace-nowrap rounded-md px-2.5 py-1 text-xs transition-all',
                    selectedModel === model
                      ? 'bg-card font-semibold text-card-foreground shadow-sm'
                      : 'text-foreground-secondary'
                  )}
                  onClick={() => {
                    setSelectedModel(model)
                    setOpenValue(RISK_CONTRIBUTORS_ANCHOR)
                  }}
                >
                  {modelLabel(model)}
                </button>
              ))}
            </fieldset>
          ) : (
            <span className="text-xs whitespace-nowrap text-foreground-secondary">
              {modelLabel(activeBreakdown.model)}
            </span>
          )}
          <RiskContributorsInfo
            bothModels={v28Available && v22Available}
            hasCmsScore={cmsScore != null}
            hasInternalScore={score !== undefined}
          />
          {(cmsScore != null || score !== undefined) && (
            <span className="grid flex-1 grid-cols-[auto_auto] items-baseline justify-end gap-x-2 gap-y-0.5 py-2.5">
              {cmsScore != null && (
                <ScoreReadout label="CMS Official Score" value={cmsScore} />
              )}
              {score !== undefined && (
                <ScoreReadout label="Internal Risk Score" value={score} />
              )}
            </span>
          )}
        </div>
        {/* The last row in each section carries its own bottom padding, so the
            content adds none of its own. */}
        <AccordionContent className="border-t pt-0 pb-0">
          {/* Column labels share the first section heading row so they remain
              visible when either category is absent. */}
          <div className={cn(ROW_GRID, 'items-center pt-3.5 pb-2')}>
            <SectionHeading>
              {conditions.length > 0 ? HEADINGS.hcc : HEADINGS.demographic}
            </SectionHeading>
            <ColumnLabel className="text-center">Seen Health</ColumnLabel>
            <ColumnLabel className="text-center">External</ColumnLabel>
            <ColumnLabel className="text-right">Coeff.</ColumnLabel>
          </div>
          {conditions.map((term) => (
            <div key={term.key} className="border-t pt-3 pb-3.5 text-xs">
              <div className={cn(ROW_GRID, 'items-baseline')}>
                <span className="min-w-0 font-semibold">{term.label}</span>
                <TermFigures term={term} />
              </div>
              {term.diagnoses.map((diagnosis) => (
                <DiagnosisRow key={diagnosis.code} diagnosis={diagnosis} />
              ))}
            </div>
          ))}

          {interactions.length > 0 && (
            <div className="mt-2.5 border-t">
              <Accordion type="single" collapsible>
                <AccordionItem value="interactions" className="border-none">
                  <AccordionTrigger
                    hideChevron
                    className={cn(
                      ROW_GRID,
                      'items-center border-b px-0 pt-4 pb-3 text-sm hover:no-underline'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <SectionHeading>{HEADINGS.interaction}</SectionHeading>
                      <IconChevronDown className="size-3.5 shrink-0 text-foreground-tertiary transition-transform duration-200 group-data-[state=open]:rotate-180" />
                    </span>
                    <span className="col-start-4 text-right tabular-nums">
                      {formatCoefficient(sumBy(interactions, 'coefficient'))}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-0 pt-0 pb-2">
                    {interactions.map((term) => (
                      <div
                        key={term.key}
                        className={cn(
                          ROW_GRID,
                          'items-baseline py-1.5 text-xs'
                        )}
                      >
                        <span className="min-w-0">{term.label}</span>
                        <TermFigures term={term} />
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          )}

          {demographics.length > 0 && conditions.length > 0 && (
            <SectionHeading className="pt-6 pb-2">
              {HEADINGS.demographic}
            </SectionHeading>
          )}
          {demographics.map((term) => (
            <div
              key={term.key}
              className={cn(ROW_GRID, 'items-baseline border-t py-2.5 text-xs')}
            >
              <span className="min-w-0">{term.label}</span>
              <TermFigures term={term} />
            </div>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  )
}

function scoresExplanation({
  hasCmsScore,
  hasInternalScore,
}: {
  hasCmsScore: boolean
  hasInternalScore: boolean
}): JSX.Element | null {
  const cms = (
    <>
      the <strong>CMS Official Score</strong> (reflecting prior-year
      documentation, which determines how we currently get paid for
      dual-eligible participants by CMS)
    </>
  )
  const internal = (
    <>
      the <strong>Internal Risk Score</strong> (reflecting current-year progress
      documented by our PCPs and external providers; per CMS rules, all
      conditions reset on January 1 and must be re-evaluated and documented in a
      face-to-face encounter every calendar year)
    </>
  )
  if (hasCmsScore && hasInternalScore) {
    return (
      <>
        You will see two scores: {cms} and {internal}.
      </>
    )
  }
  if (hasCmsScore) {
    return <>You will see {cms}.</>
  }
  if (hasInternalScore) {
    return <>You will see {internal}.</>
  }
  return null
}

const MODEL_TOGGLE_COPY =
  'The CMS-HCC v22 and v28 toggle allows you to view conditions under two different scoring systems used by CMS. This toggle is provided because CMS is phasing in v28—an updated scoring system that reduces qualifying diagnosis codes and requires higher diagnosis specificity.'

function RiskContributorsInfo({
  bothModels,
  hasCmsScore,
  hasInternalScore,
}: {
  bothModels: boolean
  hasCmsScore: boolean
  hasInternalScore: boolean
}): JSX.Element {
  const scores = scoresExplanation({ hasCmsScore, hasInternalScore })

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="About risk contributors"
          className="shrink-0 rounded-full p-0.5 text-foreground-secondary hover:bg-muted"
        >
          <IconInfoCircle className="size-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start" className="max-w-md">
        <div className="space-y-2 text-xs leading-4">
          <p>
            This section displays the demographic factors and active clinical
            conditions (HCCs) driving this participant's Risk Adjustment Factor
            (RAF) score.
          </p>
          {(scores || bothModels) && (
            <p>
              {scores}
              {bothModels && (
                <>
                  {scores ? ' ' : null}
                  {MODEL_TOGGLE_COPY}
                </>
              )}
            </p>
          )}
          <p>
            The coefficients below do not sum directly to the Internal Risk
            Score because CMS applies adjustments and a PACE frailty score;
            additionally, clinical hierarchies automatically count only the most
            severe diagnosis within a disease family.
          </p>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function ScoreReadout({
  label,
  value,
}: {
  label: string
  value: number
}): JSX.Element {
  return (
    <>
      <span className="text-xs font-normal text-foreground-secondary">
        {label}:
      </span>
      <span className="text-left font-medium text-foreground tabular-nums">
        {formatRafScore(value)}
      </span>
    </>
  )
}

function SectionHeading({
  children,
  className,
}: {
  children: string
  className?: string
}): JSX.Element {
  return (
    <h3 className={cn('font-semibold text-foreground text-sm', className)}>
      {children}
    </h3>
  )
}

function ColumnLabel({
  children,
  className,
}: {
  children: string
  className?: string
}): JSX.Element {
  return (
    <span className={cn('text-placeholder text-xs leading-4', className)}>
      {children}
    </span>
  )
}

// `col-start-4` skips the two dot columns, which only diagnosis rows fill.
function TermFigures({ term }: { term: RafTerm }): JSX.Element {
  return (
    <span className="col-start-4 text-right tabular-nums">
      {formatCoefficient(term.coefficient)}
    </span>
  )
}

function DiagnosisRow({ diagnosis }: { diagnosis: RafDiagnosis }): JSX.Element {
  return (
    <div className={cn(ROW_GRID, 'items-baseline pt-1.5')}>
      <span className="flex min-w-0 flex-wrap items-baseline gap-2.5 text-xs leading-4 text-foreground-secondary">
        <span className="shrink-0 font-mono text-foreground">
          {diagnosis.code}
        </span>
        {diagnosis.display && (
          <span className="min-w-0">{diagnosis.display}</span>
        )}
      </span>
      {/* An empty dot claims the code is absent from that source, so a code with
          no provenance at all leaves both columns blank instead. */}
      {diagnosis.sources.length > 0 && (
        <>
          <DiagnosisSourceDot
            source="seen"
            documented={diagnosis.sources.includes('seen')}
          />
          <DiagnosisSourceDot
            source="external"
            documented={diagnosis.sources.includes('external')}
          />
        </>
      )}
    </div>
  )
}

function DiagnosisSourceDot({
  source,
  documented,
}: {
  source: RafDiagnosisSource
  documented: boolean
}): JSX.Element {
  const explanation =
    SOURCE_EXPLANATIONS[source][documented ? 'documented' : 'absent']

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="flex justify-center self-center">
          <span
            role="img"
            aria-label={explanation}
            className={cn(
              'size-2 rounded-full border',
              documented
                ? 'border-placeholder bg-placeholder'
                : 'border-foreground-underline'
            )}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top">{explanation}</TooltipContent>
    </Tooltip>
  )
}
